
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProductFormData } from "@/utils/productHelpers";

interface BasicInfoTabProps {
  register: UseFormRegister<ProductFormData>;
  watch: UseFormWatch<ProductFormData>;
  setValue: UseFormSetValue<ProductFormData>;
  errors: FieldErrors<ProductFormData>;
  categories: any[];
}

const BasicInfoTab = ({ register, watch, setValue, errors, categories }: BasicInfoTabProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Product Name *</Label>
            <Input
              id="name"
              {...register("name", { required: "Product name is required" })}
              className="mt-1"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register("description")}
              className="mt-1"
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="short_description">Short Description</Label>
            <Textarea
              id="short_description"
              {...register("short_description")}
              className="mt-1"
              rows={2}
              placeholder="Brief promotional description (max 150 characters recommended)"
            />
            <p className="text-sm text-admin-muted-foreground mt-1">
              Displayed on product cards for quick overview
            </p>
          </div>

          <div>
            <Label htmlFor="sku">SKU</Label>
            <Input
              id="sku"
              {...register("sku")}
              className="mt-1"
              placeholder="Product SKU"
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              {...register("tags")}
              className="mt-1"
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-sm text-admin-muted-foreground mt-1">
              Separate tags with commas
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Pricing & Organization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="price">Price *</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              {...register("price", { required: "Price is required", min: 0 })}
              className="mt-1"
            />
            {errors.price && (
              <p className="text-sm text-red-500 mt-1">{errors.price.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="compare_at_price">Compare at Price</Label>
            <Input
              id="compare_at_price"
              type="number"
              step="0.01"
              {...register("compare_at_price", { min: 0 })}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="category_id">Category</Label>
            <Select
              value={watch("category_id")}
              onValueChange={(value) => setValue("category_id", value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Category</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={watch("status")}
              onValueChange={(value) => setValue("status", value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="featured"
              checked={watch("featured")}
              onCheckedChange={(checked) => setValue("featured", checked)}
            />
            <Label htmlFor="featured">Featured Product</Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BasicInfoTab;
